export type PlaytestStage = 1 | 2 | 3;
export type PlaytestLevel = 1 | 2 | 3 | 4 | 5;
export type InitialLevelSource = "self_assessment" | "default";

export interface PlaytestQuestionLike {
  id: string;
  source_question_id?: string | null;
  station_id?: string | null;
  stage?: number | null;
  complexity?: number | null;
  status?: string | null;
  human_review?: {
    decision?: string | null;
  } | null;
}

export interface PlaytestHistoryItem {
  questionId: string;
  stage: PlaytestStage;
  level: PlaytestLevel;
  wasCorrect: boolean;
  earnedPoints: number;
  speedBonusPoints?: number;
  answerSeconds?: number;
}

export interface PlaytestSession {
  currentStage: PlaytestStage;
  currentLevel: PlaytestLevel;
  targetStagePoints: number;
  stagePoints: number;
  stationPoints: number;
  correctTotal: number;
  incorrectTotal: number;
  consecutiveCorrect: number;
  consecutiveIncorrect: number;
  answeredQuestionIds: string[];
  activeQuestionId: string | null;
  lastQuestionId: string | null;
  history: PlaytestHistoryItem[];
  isStageComplete: boolean;
  isComplete: boolean;
  initialLevelSource: InitialLevelSource;
}

export const stageLevelPolicy: Record<PlaytestStage, PlaytestLevel[]> = {
  1: [1, 2],
  2: [2, 3],
  3: [3, 4, 5],
};

const minLevel = 1;
const maxLevel = 5;
const minStage = 1;
const maxStage = 3;

function clampNumber(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function clampStage(stage: number): PlaytestStage {
  return clampNumber(Math.trunc(stage), minStage, maxStage) as PlaytestStage;
}

export function clampLevel(level: number): PlaytestLevel {
  return clampNumber(Math.trunc(level), minLevel, maxLevel) as PlaytestLevel;
}

export function getAllowedLevels(stage: number): PlaytestLevel[] {
  return stageLevelPolicy[clampStage(stage)] ?? stageLevelPolicy[1];
}

export function clampLevelForStage(
  level: number,
  stage: number,
): PlaytestLevel {
  const normalizedLevel = clampLevel(level);
  const allowedLevels = getAllowedLevels(stage);

  if (allowedLevels.includes(normalizedLevel)) {
    return normalizedLevel;
  }

  return [...allowedLevels].sort(
    (a, b) => Math.abs(a - normalizedLevel) - Math.abs(b - normalizedLevel),
  )[0];
}

export function getStartingLevelFromAssessment(
  score: number | null | undefined,
): {
  level: PlaytestLevel;
  source: InitialLevelSource;
} {
  if (typeof score !== "number" || !Number.isFinite(score)) {
    return { level: 2, source: "default" };
  }

  if (score <= 3) return { level: 1, source: "self_assessment" };
  if (score <= 6) return { level: 2, source: "self_assessment" };
  if (score <= 8) return { level: 3, source: "self_assessment" };
  return { level: 4, source: "self_assessment" };
}

export function getQuestionSourceKey(
  question: Pick<PlaytestQuestionLike, "id" | "source_question_id">,
) {
  const sourceId = question.source_question_id?.trim();
  if (!sourceId) return question.id;

  const separatorIndex = sourceId.indexOf(":");
  return separatorIndex >= 0 ? sourceId.slice(separatorIndex + 1) : sourceId;
}

export function createPlaytestSession(
  options: {
    startingStage?: number;
    startingLevel?: number;
    targetStagePoints?: number;
    initialLevelSource?: InitialLevelSource;
  } = {},
): PlaytestSession {
  const currentStage = clampStage(options.startingStage ?? 1);

  return {
    currentStage,
    currentLevel: clampLevelForStage(options.startingLevel ?? 2, currentStage),
    targetStagePoints: options.targetStagePoints ?? 10,
    stagePoints: 0,
    stationPoints: 0,
    correctTotal: 0,
    incorrectTotal: 0,
    consecutiveCorrect: 0,
    consecutiveIncorrect: 0,
    answeredQuestionIds: [],
    activeQuestionId: null,
    lastQuestionId: null,
    history: [],
    isStageComplete: false,
    isComplete: false,
    initialLevelSource: options.initialLevelSource ?? "default",
  };
}

export function applyPlaytestAnswer(
  session: PlaytestSession,
  question: Pick<PlaytestQuestionLike, "id" | "stage" | "complexity">,
  isCorrect: boolean,
  options: {
    speedBonusPoints?: number;
    answerSeconds?: number;
    requiredQuestionCount?: number;
    passingPercent?: number;
  } = {},
): PlaytestSession {
  const questionLevel = clampLevel(question.complexity ?? session.currentLevel);
  const questionStage = clampStage(question.stage ?? session.currentStage);
  const speedBonusPoints = isCorrect
    ? Math.max(0, Math.trunc(options.speedBonusPoints ?? 0))
    : 0;
  const earnedPoints = isCorrect ? questionLevel + speedBonusPoints : 0;
  const stagePoints = Math.min(
    session.targetStagePoints,
    session.stagePoints + earnedPoints,
  );
  const stationPoints = session.stationPoints + earnedPoints;
  const correctTotal = session.correctTotal + (isCorrect ? 1 : 0);
  const incorrectTotal = session.incorrectTotal + (isCorrect ? 0 : 1);
  let currentLevel = clampLevelForStage(
    session.currentLevel,
    session.currentStage,
  );
  let consecutiveCorrect = isCorrect ? session.consecutiveCorrect + 1 : 0;
  let consecutiveIncorrect = isCorrect ? 0 : session.consecutiveIncorrect + 1;

  if (consecutiveCorrect >= 2) {
    currentLevel = clampLevelForStage(currentLevel + 1, session.currentStage);
    consecutiveCorrect = 0;
    consecutiveIncorrect = 0;
  }

  if (consecutiveIncorrect >= 2) {
    currentLevel = clampLevelForStage(currentLevel - 1, session.currentStage);
    consecutiveCorrect = 0;
    consecutiveIncorrect = 0;
  }

  const answeredQuestionIds = [...session.answeredQuestionIds, question.id];
  const history = [
    ...session.history,
    {
      questionId: question.id,
      stage: questionStage,
      level: questionLevel,
      wasCorrect: isCorrect,
      earnedPoints,
      speedBonusPoints,
      answerSeconds: options.answerSeconds,
    },
  ];
  const requiredQuestionCount = Math.max(
    0,
    Math.trunc(options.requiredQuestionCount ?? 0),
  );
  const passingPercent = clampNumber(options.passingPercent ?? 70, 0, 100);
  const stageAnsweredCount = history.filter(
    (item) => item.stage === questionStage,
  ).length;
  const stageCorrectCount = history.filter(
    (item) => item.stage === questionStage && item.wasCorrect,
  ).length;
  const stageAccuracy =
    requiredQuestionCount > 0
      ? (stageCorrectCount / requiredQuestionCount) * 100
      : 0;
  const isStageComplete =
    requiredQuestionCount > 0
      ? stageAnsweredCount >= requiredQuestionCount &&
        stageAccuracy >= passingPercent
      : stagePoints >= session.targetStagePoints;

  return {
    ...session,
    currentLevel,
    stagePoints,
    stationPoints,
    correctTotal,
    incorrectTotal,
    consecutiveCorrect,
    consecutiveIncorrect,
    answeredQuestionIds,
    activeQuestionId: question.id,
    lastQuestionId: question.id,
    history,
    isStageComplete,
    isComplete: session.currentStage >= maxStage && isStageComplete,
  };
}

export function advancePlaytestStage(
  session: PlaytestSession,
): PlaytestSession {
  if (!session.isStageComplete || session.currentStage >= maxStage) {
    return session;
  }

  const nextStage = clampStage(session.currentStage + 1);

  return {
    ...session,
    currentStage: nextStage,
    currentLevel: clampLevelForStage(3, nextStage),
    stagePoints: 0,
    consecutiveCorrect: 0,
    consecutiveIncorrect: 0,
    answeredQuestionIds: [],
    activeQuestionId: null,
    lastQuestionId: null,
    isStageComplete: false,
    isComplete: false,
  };
}

export function advancePlaytestQuestion(
  session: PlaytestSession,
): PlaytestSession {
  return {
    ...session,
    activeQuestionId: null,
  };
}

export function getPlayableQuestions(
  questions: PlaytestQuestionLike[],
  stationId: string | null | undefined,
  stage?: number,
): PlaytestQuestionLike[] {
  if (!stationId) return [];

  return questions
    .filter((question) => {
      const questionStage = question.stage ?? stage;
      const complexity = question.complexity;

      if (!questionStage || !complexity) return false;

      return (
        question.station_id === stationId &&
        (stage === undefined || questionStage === stage) &&
        getAllowedLevels(questionStage).includes(clampLevel(complexity)) &&
        question.status === "approved" &&
        question.human_review?.decision === "approved"
      );
    })
    .sort((a, b) => a.id.localeCompare(b.id));
}

export function getPlaytestQuestion(
  questions: PlaytestQuestionLike[],
  session: PlaytestSession,
  stationId: string | null | undefined,
  rng: () => number = Math.random,
  options: { avoidSourceKeys?: Set<string> } = {},
): PlaytestQuestionLike | undefined {
  const playableQuestions = getPlayableQuestions(
    questions,
    stationId,
    session.currentStage,
  );
  const activeQuestion = playableQuestions.find(
    (question) => question.id === session.activeQuestionId,
  );

  if (activeQuestion) {
    return activeQuestion;
  }

  const unansweredQuestions = playableQuestions.filter(
    (question) => !session.answeredQuestionIds.includes(question.id),
  );

  if (unansweredQuestions.length === 0) {
    return undefined;
  }

  const currentLevel = clampLevelForStage(
    session.currentLevel,
    session.currentStage,
  );
  const exactLevelCandidates = unansweredQuestions.filter(
    (question) => question.complexity === currentLevel,
  );
  const candidatesByNearestLevel = [...unansweredQuestions].sort(
    (a, b) =>
      Math.abs(clampLevel(a.complexity ?? currentLevel) - currentLevel) -
      Math.abs(clampLevel(b.complexity ?? currentLevel) - currentLevel),
  );
  const nearestDistance = candidatesByNearestLevel[0]
    ? Math.abs(
        clampLevel(candidatesByNearestLevel[0].complexity ?? currentLevel) -
          currentLevel,
      )
    : 0;
  const nearestLevelCandidates = candidatesByNearestLevel.filter(
    (question) =>
      Math.abs(
        clampLevel(question.complexity ?? currentLevel) - currentLevel,
      ) === nearestDistance,
  );
  const levelCandidates =
    exactLevelCandidates.length > 0
      ? exactLevelCandidates
      : nearestLevelCandidates;
  const sourceFilteredCandidates = levelCandidates.filter(
    (question) => !options.avoidSourceKeys?.has(getQuestionSourceKey(question)),
  );
  const selectableCandidates =
    sourceFilteredCandidates.length > 0
      ? sourceFilteredCandidates
      : levelCandidates;
  const nonRepeatingCandidates = selectableCandidates.filter(
    (question) => question.id !== session.lastQuestionId,
  );
  const randomCandidates =
    nonRepeatingCandidates.length > 0
      ? nonRepeatingCandidates
      : selectableCandidates;

  if (randomCandidates.length === 0) {
    return undefined;
  }

  const index = Math.min(
    randomCandidates.length - 1,
    Math.floor(rng() * randomCandidates.length),
  );
  return randomCandidates[index];
}
